import { ObjectTools } from '../../../tools/object-tools';
import { PopoverTools } from '../../../tools/popover-tools';
import { SolPopover, SolRectangle } from '../../../services/popover.service';

export interface HostStyle {
  position?: string;
  top?: string;
  left?: string;
  overflow?: string;
}

export interface PopoverStyle {
  width?: string;
  minWidth?: string;
  maxWidth?: string;
}

export interface ContentStyle {
  maxHeight?: string;
}

export enum SolPopoverAlignment {
  bottomLeft = 'bottomLeft',
  bottomRight = 'bottomRight',
  topLeft = 'topLeft',
  topRight = 'topRight',
}

export class SolPopoverState {
  private static readonly VIEWPORT_MARGIN = 16;

  private constructor(
    public readonly data: SolPopover<any> | undefined,
    public readonly hover: boolean,
    public readonly parentHover: boolean,
    public readonly hoverOut: boolean,
    public readonly hostStyle: HostStyle,
    public readonly popoverStyle: PopoverStyle,
    public readonly contentStyle: ContentStyle,
    public readonly containerClass: string,
    public readonly popoverClass: string,
    public readonly popoverWidth: number,
    public readonly popoverHeight: number,
    public readonly clientWidth: number,
    public readonly clientHeight: number,
    public readonly fixed: boolean,
    public readonly popoverVisible: boolean,
    public readonly bottomLeftReserve: number,
  ) {}

  /**
   * @param bottomLeftReserve Size in px of a square in the viewport's bottom-left corner that popovers stay out of.
   */
  public static of(bottomLeftReserve: number): SolPopoverState {
    return new SolPopoverState(
      undefined,
      false,
      false,
      false,
      {},
      {},
      {},
      'sol-popover-hidden',
      '',
      0,
      0,
      0,
      0,
      false,
      false,
      bottomLeftReserve,
    );
  }

  public withData(data: SolPopover<any> | undefined): SolPopoverState {
    return this.copy({ data }).recalculate();
  }

  public withHover(hover: boolean): SolPopoverState {
    return this.copy({ hover }).recalculate();
  }

  public withParentHover(parentHover: boolean): SolPopoverState {
    return this.copy({ parentHover }).recalculate();
  }

  public withResize(
    popoverWidth: number,
    popoverHeight: number,
    clientWidth: number,
    clientHeight: number,
  ): SolPopoverState {
    return this.copy({ popoverWidth, popoverHeight, clientWidth, clientHeight }).recalculate();
  }

  private recalculate(): SolPopoverState {
    const fixed = this.data?.options.bounds !== undefined;
    const popoverVisible = !!this.data;
    const hoverOut = !!this.data?.options.closeOnHoverOut && !this.hover && !this.parentHover;

    // No popover present
    if (!this.data || this.popoverWidth === 0 || this.popoverHeight === 0) {
      return this.copy({
        containerClass: 'sol-popover-hidden',
        hostStyle: {},
        popoverClass: '',
        popoverStyle: {},
        fixed,
        popoverVisible,
        hoverOut,
      });
    }

    // Calculate position based on bounds
    const clientWidth = this.clientWidth;
    const clientHeight = this.clientHeight;
    let parent: SolRectangle;
    let align: SolPopoverAlignment;
    let hostX: number;
    let hostY: number;
    if (PopoverTools.isValidParent(this.data.options.bounds)) {
      // Parent
      parent = this.data.options.bounds;

      // Calculate alignment options
      const canAlignLeft = parent.x + this.popoverWidth <= clientWidth;
      const canAlignRight = parent.x + parent.width - this.popoverWidth >= 0;
      const gapY = this.data.options.gapY;
      const spaceAbove = this.toSpaceAbove(parent.y, gapY);
      const spaceBelowLeft = this.toSpaceBelow(parent.y + parent.height, gapY, parent.x, clientHeight);
      const spaceBelowRight = this.toSpaceBelow(
        parent.y + parent.height,
        gapY,
        parent.x + parent.width - this.popoverWidth,
        clientHeight,
      );
      const canAlignTop = this.popoverHeight <= spaceAbove;
      const canAlignBottomLeft = canAlignLeft && this.popoverHeight <= spaceBelowLeft;
      const canAlignBottomRight = canAlignRight && this.popoverHeight <= spaceBelowRight;
      const canAlignTopLeft = canAlignLeft && canAlignTop;
      const canAlignTopRight = canAlignRight && canAlignTop;

      // Can match exact alignment
      const wantAlign = this.data.options.align;
      if (wantAlign === SolPopoverAlignment.bottomLeft && canAlignBottomLeft) {
        align = SolPopoverAlignment.bottomLeft;
      } else if (wantAlign === SolPopoverAlignment.bottomRight && canAlignBottomRight) {
        align = SolPopoverAlignment.bottomRight;
      } else if (wantAlign === SolPopoverAlignment.topLeft && canAlignTopLeft) {
        align = SolPopoverAlignment.topLeft;
      } else if (wantAlign === SolPopoverAlignment.topRight && canAlignTopRight) {
        align = SolPopoverAlignment.topRight;

        // Can match bottom/top alignment
      } else if (wantAlign === SolPopoverAlignment.bottomRight && canAlignBottomLeft) {
        align = SolPopoverAlignment.bottomLeft;
      } else if (wantAlign === SolPopoverAlignment.bottomLeft && canAlignBottomRight) {
        align = SolPopoverAlignment.bottomRight;
      } else if (wantAlign === SolPopoverAlignment.topRight && canAlignTopLeft) {
        align = SolPopoverAlignment.topLeft;
      } else if (wantAlign === SolPopoverAlignment.topLeft && canAlignTopRight) {
        align = SolPopoverAlignment.topRight;

        // Can match left/right alignment
      } else if (wantAlign === SolPopoverAlignment.topLeft && canAlignBottomLeft) {
        align = SolPopoverAlignment.bottomLeft;
      } else if (wantAlign === SolPopoverAlignment.topRight && canAlignBottomRight) {
        align = SolPopoverAlignment.bottomRight;
      } else if (wantAlign === SolPopoverAlignment.bottomLeft && canAlignTopLeft) {
        align = SolPopoverAlignment.topLeft;
      } else if (wantAlign === SolPopoverAlignment.bottomRight && canAlignTopRight) {
        align = SolPopoverAlignment.topRight;

        // Fallback in order of preference
      } else if (canAlignBottomLeft) {
        align = SolPopoverAlignment.bottomLeft;
      } else if (canAlignBottomRight) {
        align = SolPopoverAlignment.bottomRight;
      } else if (canAlignTopLeft) {
        align = SolPopoverAlignment.topLeft;
      } else if (canAlignTopRight) {
        align = SolPopoverAlignment.topRight;
      } else if (spaceAbove > spaceBelowLeft) {
        align = SolPopoverAlignment.topLeft;
      } else {
        align = SolPopoverAlignment.bottomLeft;
      }

      // Calculate position
      hostX =
        align === SolPopoverAlignment.bottomLeft || align === SolPopoverAlignment.topLeft
          ? parent.x
          : parent.x + parent.width;
      hostY =
        align === SolPopoverAlignment.topLeft || align === SolPopoverAlignment.topRight
          ? parent.y
          : parent.y + parent.height;
    } else {
      // parent & alignment
      parent = {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      };
      align = this.data.options.align;

      // Calculate position
      hostX = align === SolPopoverAlignment.bottomLeft || align === SolPopoverAlignment.topLeft ? 0 : clientWidth - 100;
      hostY = align === SolPopoverAlignment.topLeft || align === SolPopoverAlignment.topRight ? 0 : clientHeight - 100;
    }

    // Calculate style
    const deltaX =
      this.data.options.gapX *
      (align === SolPopoverAlignment.topRight || align === SolPopoverAlignment.bottomRight ? -1 : 1);
    const deltaY =
      this.data.options.gapY *
      (align === SolPopoverAlignment.topLeft || align === SolPopoverAlignment.topRight ? -1 : 1);
    const alignsRight = align === SolPopoverAlignment.topRight || align === SolPopoverAlignment.bottomRight;
    const hostLeft = this.toClampedX(hostX + deltaX, align, clientWidth);
    const popoverLeft = alignsRight ? hostLeft - this.popoverWidth : hostLeft;
    const hostStyle = {
      position: 'fixed',
      overflow: 'hidden',
      left: `${hostLeft}px`,
      top: `${hostY + deltaY}px`,
    };

    // Calculate max bounds
    const maxHeight =
      align === SolPopoverAlignment.topLeft || align === SolPopoverAlignment.topRight
        ? this.toSpaceAbove(hostY, this.data.options.gapY)
        : this.toSpaceBelow(hostY, this.data.options.gapY, popoverLeft, clientHeight);

    // sol-popover-container
    const error = this.data.options.error;
    const containerErrorClass = error ? ' sol-popover-container-error' : '';
    const containerHoverClass = this.hover ? ' sol-popover-container-hidden' : '';
    const containerClass =
      'sol-popover-container-' + this.toClassSuffix(align) + containerErrorClass + containerHoverClass;

    // sol-popover
    const errorClass = error ? ' sol-popover-error' : '';
    const containerFadeInfClass = this.data.options.fadeIn ? ' sol-popover-fade-in' : '';
    const popoverClass = 'sol-popover-' + this.toClassSuffix(align) + errorClass + containerFadeInfClass;

    // Popover style
    const popoverWidth = this.data.options.width;
    const popoverStyle = {
      width: popoverWidth === 'parent-grow' ? 'max-content' : 'auto',
      minWidth: popoverWidth === 'parent' || popoverWidth === 'parent-grow' ? `${parent.width}px` : 'auto',
      maxWidth: popoverWidth === 'parent' ? `${parent.width}px` : 'auto',
    };

    // Content style
    const contentStyle = {
      maxHeight: `${maxHeight}px`,
    };

    return this.copy({
      containerClass,
      hostStyle,
      popoverClass,
      popoverStyle,
      contentStyle,
      fixed,
      popoverVisible,
      hoverOut,
    });
  }

  private toSpaceAbove(hostY: number, gapY: number): number {
    return hostY - gapY - SolPopoverState.VIEWPORT_MARGIN;
  }

  private toSpaceBelow(hostY: number, gapY: number, popoverLeft: number, clientHeight: number): number {
    return clientHeight - (hostY + gapY) - SolPopoverState.VIEWPORT_MARGIN - this.toBottomReserve(popoverLeft);
  }

  private toBottomReserve(popoverLeft: number): number {
    return popoverLeft < this.bottomLeftReserve ? this.bottomLeftReserve : 0;
  }

  private toClampedX(x: number, align: SolPopoverAlignment, clientWidth: number): number {
    const alignsRight = align === SolPopoverAlignment.topRight || align === SolPopoverAlignment.bottomRight;
    const left = alignsRight ? x - this.popoverWidth : x;
    const clampedLeft = Math.max(0, Math.min(left, clientWidth - this.popoverWidth));
    return alignsRight ? clampedLeft + this.popoverWidth : clampedLeft;
  }

  private toClassSuffix(align: SolPopoverAlignment): string {
    switch (align) {
      case SolPopoverAlignment.bottomLeft:
        return 'bottom-left';
      case SolPopoverAlignment.bottomRight:
        return 'bottom-right';
      case SolPopoverAlignment.topLeft:
        return 'top-left';
      case SolPopoverAlignment.topRight:
        return 'top-right';
    }
  }

  private copy(props: Partial<SolPopoverState>): SolPopoverState {
    return ObjectTools.copy<SolPopoverState>(this, props);
  }
}
